import firebase from 'firebase';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, MAX_TRANSLATIONS_PER_PROCEDURE } from './constants';

export async function getProcedureTranslation(procedureID, lang, includeDisabled = false) {
    if (!lang || lang === DEFAULT_LANGUAGE) {
        return null;
    }
    
    if (!SUPPORTED_LANGUAGES.includes(lang)) {
        console.warn(`Idioma no soportado: ${lang}`);
        return null;
    }
    
    try {
        const translationDoc = await firebase.firestore()
            .collection("procedures")
            .doc(procedureID)
            .collection("translations")
            .doc(lang)
            .get();
        
        if (!translationDoc.exists) {
            return null;
        }
        
        const translationData = translationDoc.data();
        
        if (!includeDisabled && translationData.enabled === false) {
            return null;
        }
        
        return translationData;
    } catch (error) {
        console.log(`Error obtener traducción: ${JSON.stringify(error)}`);
        return null;
    }
}

export async function listProcedureTranslations(procedureID, includeDisabled = true) {
    try {
        const translationsSnapshot = await firebase.firestore()
            .collection("procedures")
            .doc(procedureID)
            .collection("translations")
            .get();
        
        const translations = {};
        translationsSnapshot.docs.forEach(doc => {
            const translationData = doc.data();
            if (includeDisabled || translationData.enabled !== false) {
                translations[doc.id] = translationData;
            }
        });
        
        return translations;
    } catch (error) {
        console.log(`Error listar traducciones: ${JSON.stringify(error)}`);
        return {};
    }
}

export async function listEnabledProcedureTranslations(procedureID) {
    return await listProcedureTranslations(procedureID, false);
}

export async function getEnabledTranslationLanguages(procedureID) {
    try {
        const translations = await listEnabledProcedureTranslations(procedureID);
        const languages = [DEFAULT_LANGUAGE];
        
        Object.keys(translations).forEach(lang => {
            if (lang !== DEFAULT_LANGUAGE && SUPPORTED_LANGUAGES.includes(lang)) {
                languages.push(lang);
            }
        });
        
        return languages;
    } catch (error) {
        console.log(`Error obtener idiomas habilitados: ${JSON.stringify(error)}`);
        return [DEFAULT_LANGUAGE];
    }
}

export async function saveProcedureTranslation(procedureID, lang, translationData) {
    if (!SUPPORTED_LANGUAGES.includes(lang)) {
        throw new Error(`Idioma no soportado: ${lang}`);
    }
    
    if (lang === DEFAULT_LANGUAGE) {
        throw new Error(`No se puede crear traducción para el idioma por defecto (${DEFAULT_LANGUAGE})`);
    }
    
    const existingTranslations = await listProcedureTranslations(procedureID);
    const translationCount = Object.keys(existingTranslations).length;
    
    if (translationCount >= MAX_TRANSLATIONS_PER_PROCEDURE) {
        const isUpdating = existingTranslations[lang];
        if (!isUpdating) {
            throw new Error(`Se alcanzó el límite máximo de ${MAX_TRANSLATIONS_PER_PROCEDURE} traducciones por procedimiento`);
        }
    }
    
    try {
        await firebase.firestore()
            .collection("procedures")
            .doc(procedureID)
            .collection("translations")
            .doc(lang)
            .set(translationData, { merge: true });
        
        return true;
    } catch (error) {
        console.log(`Error guardar traducción: ${JSON.stringify(error)}`);
        throw error;
    }
}

export async function deleteProcedureTranslation(procedureID, lang) {
    if (lang === DEFAULT_LANGUAGE) {
        throw new Error(`No se puede eliminar el idioma por defecto (${DEFAULT_LANGUAGE})`);
    }
    
    try {
        await firebase.firestore()
            .collection("procedures")
            .doc(procedureID)
            .collection("translations")
            .doc(lang)
            .delete();
        
        return true;
    } catch (error) {
        console.log(`Error eliminar traducción: ${JSON.stringify(error)}`);
        throw error;
    }
}

export function applyProcedureTranslation(procedure, translation, lang = DEFAULT_LANGUAGE) {
    if (!procedure) {
        return null;
    }
    
    if (!translation || lang === DEFAULT_LANGUAGE) {
        return procedure;
    }
    
    const translatedProcedure = { ...procedure };
    
    if (translation.name) {
        translatedProcedure.name = translation.name;
    }
    
    if (translation.detail) {
        translatedProcedure.detail = translation.detail;
    }
    
    if (translation.video) {
        translatedProcedure.video = translation.video;
    }
    
    if (translation.demo) {
        translatedProcedure.demo = translation.demo;
    }
    
    if (translation.fascicle) {
        translatedProcedure.fascicle = translation.fascicle;
    }
    
    if (translation.times && Array.isArray(translation.times)) {
        translatedProcedure.times = translation.times;
    }
    
    if (translation.meta) {
        translatedProcedure.meta = translation.meta;
    }
    
    return translatedProcedure;
}
